import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Currency } from "../../api/stripe";
import { CheckoutPanel } from "./CheckoutPanel";

export interface CheckoutSidebarProps {
  open: boolean;
  onClose: () => void;
  /** Initial currency selection inside the panel. Defaults to USD. */
  defaultCurrency?: Currency;
}

/** Slide-in checkout overlay.
 *  Desktop: right-side sidebar.
 *  Mobile (≤760px): bottom sheet — sliding up from the bottom edge.
 *  Closes on backdrop click, Escape, or the close button.
 *  Locks body scroll while open. */
export const CheckoutSidebar: React.FC<CheckoutSidebarProps> = ({
  open,
  onClose,
  defaultCurrency,
}) => {
  const { t } = useTranslation("checkout");
  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <div
        className={`mc-overlay ${open ? "mc-overlay--open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`mc-sheet ${open ? "mc-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <button
          type="button"
          className="mc-sheet__close"
          onClick={onClose}
          aria-label={t("close")}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
            <path
              fill="currentColor"
              d="M12.7 3.3a1 1 0 0 0-1.4 0L8 6.6 4.7 3.3a1 1 0 1 0-1.4 1.4L6.6 8l-3.3 3.3a1 1 0 1 0 1.4 1.4L8 9.4l3.3 3.3a1 1 0 0 0 1.4-1.4L9.4 8l3.3-3.3a1 1 0 0 0 0-1.4z"
            />
          </svg>
        </button>
        <div className="mc-sheet__handle" aria-hidden="true" />
        <div className="mc-sheet__inner">
          <CheckoutPanel defaultCurrency={defaultCurrency} />
        </div>
      </aside>
    </>
  );
};
